/**
 * UserManagement.jsx — style violet profond comme le reste de l'admin
 * Fond #1a1035, cards #2a1f4a, accents colorés vifs
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Loader2, Mail, User as UserIcon, Lock, Shield,
  RefreshCw, Users, TrendingUp, X, Clock, Globe,
  Trash2, Ban, Search, Zap, Smartphone, Download,
  Power, Wifi, BarChart2, MapPin, Activity,
  CheckCircle2, ChevronLeft, ChevronRight, Eye,
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { API_URL } from '../../config';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt     = (iso) => { try { return new Date(iso).toLocaleDateString('fr-FR',{day:'2-digit',month:'short',year:'numeric'}); } catch { return '—'; }};
const fmtFull = (iso) => { try { return new Date(iso).toLocaleString('fr-FR',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}); } catch { return '—'; }};
const fmtAgo  = (iso) => {
  if (!iso) return '—';
  const s=Math.floor((Date.now()-new Date(iso))/1000);
  if(s<5) return 'maintenant'; if(s<60) return `${s}s`;
  const m=Math.floor(s/60); if(m<60) return `${m}min`;
  const h=Math.floor(m/60); if(h<24) return `${h}h`; return `${Math.floor(h/24)}j`;
};
const fmtDur  = (s) => { if(!s||s<=0) return '—'; if(s<60) return `${s}s`; if(s<3600) return `${Math.floor(s/60)}min`; return `${Math.floor(s/3600)}h${Math.floor((s%3600)/60)}min`; };
const fmtTime = (d) => { try { return d.toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit',second:'2-digit'}); } catch { return ''; }};
const authH   = () => ({'Authorization':`Bearer ${localStorage.getItem('auth_token')}`,'Content-Type':'application/json'});
const PAGE_SIZE = 100;

// ─── Palette ─────────────────────────────────────────────────────────────────
const C = {
  bg:       '#150d2e',
  surface:  '#1e1540',
  card:     '#241b4a',
  cardHov:  '#2d2260',
  border:   'rgba(139,92,246,0.2)',
  borderHov:'rgba(139,92,246,0.4)',
  text:     '#f0ebff',
  textSub:  'rgba(200,185,255,0.6)',
  textMuted:'rgba(160,140,220,0.4)',
};

// ─── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
.um * { font-family: 'Plus Jakarta Sans', sans-serif; box-sizing: border-box; }
.um-mono { font-family: 'Courier New', monospace; }

@keyframes live-ring {
  0%   { transform:scale(1); opacity:.7; }
  100% { transform:scale(2.4); opacity:0; }
}
@keyframes fadeup {
  from { opacity:0; transform:translateY(8px); }
  to   { opacity:1; transform:translateY(0); }
}
@keyframes slidein {
  from { opacity:0; transform:translateX(32px); }
  to   { opacity:1; transform:translateX(0); }
}
@keyframes spin { to { transform:rotate(360deg); } }
@keyframes shimmer {
  0%   { background-position:-400px 0; }
  100% { background-position:400px 0; }
}

.um-fadeup  { animation:fadeup  .22s ease both; }
.um-slidein { animation:slidein .28s cubic-bezier(.16,1,.3,1) both; }
.um-row     { animation:fadeup  .18s ease both; }
.um-spin    { animation:spin .8s linear infinite; }

.um-live::after {
  content:'';
  position:absolute; inset:-3px;
  border-radius:50%;
  background:#10b981;
  opacity:.6;
  animation:live-ring 1.8s ease-out infinite;
}

.um-kpi {
  transition:transform .2s cubic-bezier(.34,1.56,.64,1), box-shadow .2s;
  cursor:default;
}
.um-kpi:hover { transform:translateY(-3px) scale(1.02); }

.um-tr { transition:background .12s; cursor:pointer; }
.um-tr:hover td { background:#2d2260 !important; }
.um-tr:hover .um-act { opacity:1; }
.um-act { opacity:0; transition:opacity .15s; }

.um-btn { transition:all .15s cubic-bezier(.34,1.56,.64,1); }
.um-btn:hover { transform:scale(1.08); }
.um-btn:active { transform:scale(.95); }

.um-pill { transition:all .15s cubic-bezier(.34,1.56,.64,1); }
.um-pill:hover { transform:translateY(-1px); }

.um-input {
  background:rgba(255,255,255,0.05) !important;
  border:1.5px solid rgba(139,92,246,0.25) !important;
  color:#f0ebff !important;
  transition:border-color .2s, box-shadow .2s;
}
.um-input::placeholder { color:rgba(160,140,220,0.4); }
.um-input:focus {
  outline:none !important;
  border-color:rgba(139,92,246,0.7) !important;
  box-shadow:0 0 0 3px rgba(139,92,246,0.15) !important;
}

.um-bar { background:rgba(139,92,246,0.15); }
.um-bar-fill { transition:width .7s cubic-bezier(.34,1.56,.64,1); }

.um-scroll::-webkit-scrollbar { width:4px; height:4px; }
.um-scroll::-webkit-scrollbar-track { background:transparent; }
.um-scroll::-webkit-scrollbar-thumb { background:rgba(139,92,246,0.3); border-radius:10px; }
.um-scroll::-webkit-scrollbar-thumb:hover { background:rgba(139,92,246,0.5); }

.um-thead th {
  position:sticky !important;
  top:0 !important;
  z-index:20 !important;
  background:#1e1540 !important;
  box-shadow:0 2px 0 rgba(139,92,246,0.2) !important;
}
`;

// ─── Atoms ───────────────────────────────────────────────────────────────────
const LiveDot = ({size=8}) => (
  <span className="relative inline-flex flex-shrink-0 um-live" style={{width:size,height:size}}>
    <span className="relative inline-flex rounded-full w-full h-full" style={{background:'#10b981'}}/>
  </span>
);

const Spinner = ({size=20,c='#8b5cf6'}) => (
  <div className="um-spin rounded-full border-2 flex-shrink-0" style={{width:size,height:size,borderColor:`${c}25`,borderTopColor:c}}/>
);

const Pip = ({children,active,onClick,color='#8b5cf6'}) => (
  <button onClick={onClick} className="um-pill px-3.5 py-1.5 rounded-xl text-xs font-bold"
    style={active
      ?{background:`linear-gradient(135deg,${color},${color}cc)`,color:'#fff',border:`1px solid ${color}`,boxShadow:`0 4px 14px ${color}40`}
      :{background:'rgba(255,255,255,0.05)',color:'rgba(200,185,255,0.55)',border:'1px solid rgba(139,92,246,0.2)'}}>
    {children}
  </button>
);

const MiniBar = ({val,max,color='#8b5cf6',h=4}) => (
  <div className="um-bar rounded-full overflow-hidden" style={{height:h}}>
    <div className="um-bar-fill h-full rounded-full" style={{width:`${max?Math.min(100,val/max*100):0}%`,background:`linear-gradient(90deg,${color}99,${color})`}}/>
  </div>
);

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({label,value,icon:Icon,gradient,live,sub,idx=0}) => (
  <div className="um-kpi rounded-2xl p-5 relative overflow-hidden" style={{
    background:`linear-gradient(135deg,${gradient[0]},${gradient[1]})`,
    boxShadow:`0 8px 24px ${gradient[0]}50`,
    animationDelay:`${idx*.06}s`,
  }}>
    <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full" style={{background:'rgba(255,255,255,0.1)'}}/>
    <div className="absolute -bottom-6 -right-2 w-12 h-12 rounded-full" style={{background:'rgba(255,255,255,0.06)'}}/>
    <div className="relative flex items-start justify-between mb-3">
      <Icon size={22} className="text-white opacity-90"/>
      {live&&value>0&&<LiveDot size={8}/>}
    </div>
    <div className="relative text-3xl font-black text-white">{value??'—'}</div>
    <div className="relative text-white/70 text-xs font-bold uppercase tracking-wider mt-1">{label}</div>
    {sub&&<div className="relative text-white/50 text-xs mt-0.5">{sub}</div>}
  </div>
);

const Sparkline = ({data,color='#10b981',h=48}) => {
  if(!data?.length) return null;
  const vals=data.map(d=>d.new_users||0), max=Math.max(...vals,1), w=280;
  const pts=vals.map((v,i)=>`${(i/(vals.length-1||1))*w},${h-(v/max)*(h-6)-3}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{height:h}}>
      <defs><linearGradient id="sg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity=".3"/><stop offset="100%" stopColor={color} stopOpacity="0"/></linearGradient></defs>
      <polyline fill="url(#sg)" stroke="none" points={`0,${h} ${pts} ${w},${h}`}/>
      <polyline fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" points={pts}/>
    </svg>
  );
};

// ─── Usage Panel ──────────────────────────────────────────────────────────────
function UsagePanel({uid,name,onClose}) {
  const [data,setData]=useState(null),[loading,setLoading]=useState(true);
  useEffect(()=>{
    fetch(`${API_URL}/tracking/admin/user/${uid}/usage`,{headers:authH()})
      .then(r=>r.json()).then(j=>{setData(j.usage);setLoading(false);}).catch(()=>setLoading(false));
  },[uid]);
  const services=data?.services||[], maxT=Math.max(...services.map(s=>s.time_sec||0),1);
  const SC={assistant:'#00d4aa',marketing_studio:'#a855f7',video_ads:'#ec4899',cv_generator:'#6366f1',companion:'#f59e0b',translator:'#06b6d4',dashboard:'#8b5cf6'};
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{background:'rgba(10,6,24,0.85)',backdropFilter:'blur(12px)'}}>
      <div className="absolute inset-0" onClick={onClose}/>
      <div className="um-fadeup relative w-full max-w-lg max-h-[85vh] flex flex-col rounded-3xl overflow-hidden z-10"
        style={{background:'#1e1540',border:'1px solid rgba(139,92,246,0.3)',boxShadow:'0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(139,92,246,0.1)'}}>
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{borderBottom:'1px solid rgba(139,92,246,0.15)'}}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{background:'linear-gradient(135deg,#7c3aed,#a855f7)'}}><BarChart2 size={16} className="text-white"/></div>
            <div><div style={{color:C.text}} className="font-bold text-sm">Utilisation détaillée</div><div style={{color:C.textSub}} className="text-xs">{name}</div></div>
          </div>
          <button onClick={onClose} className="um-btn w-8 h-8 rounded-xl flex items-center justify-center" style={{background:'rgba(255,255,255,0.06)',border:'1px solid rgba(139,92,246,0.2)'}}><X size={14} style={{color:C.textSub}}/></button>
        </div>
        {loading?<div className="flex justify-center items-center py-16"><Spinner/></div>:
        !data?<div className="text-center py-12 text-sm" style={{color:C.textMuted}}>Aucune donnée</div>:
        <div className="overflow-y-auto um-scroll p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {[{l:'Temps total',v:data.total_time_fmt,g:['#7c3aed','#a855f7']},{l:'Sessions',v:data.session_count,g:['#0d9488','#10b981']}].map((k,i)=>(
              <div key={i} className="rounded-2xl p-4 text-center" style={{background:`linear-gradient(135deg,${k.g[0]}30,${k.g[1]}15)`,border:`1px solid ${k.g[0]}30`}}>
                <div className="text-2xl font-black" style={{color:k.g[1]}}>{k.v}</div>
                <div className="text-xs font-bold uppercase tracking-wider mt-1" style={{color:`${k.g[1]}90`}}>{k.l}</div>
              </div>
            ))}
          </div>
          {services.length>0&&<div>
            <div className="text-xs font-bold uppercase tracking-widest mb-3" style={{color:C.textMuted}}>Temps par service</div>
            <div className="space-y-3">
              {services.map((s,i)=>(
                <div key={i} className="rounded-2xl p-3.5" style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(139,92,246,0.15)'}}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2"><span>{s.icon}</span><span style={{color:C.text}} className="text-sm font-semibold">{s.label}</span></div>
                    <div className="flex items-center gap-2"><span style={{color:C.textMuted}} className="text-xs">{s.pct}%</span><span className="font-black text-sm" style={{color:SC[s.service]||'#8b5cf6'}}>{s.time_fmt}</span></div>
                  </div>
                  <MiniBar val={s.time_sec} max={maxT} color={SC[s.service]||'#8b5cf6'} h={3}/>
                  {s.details&&<div className="mt-3 grid grid-cols-2 gap-2">
                    {s.service==='assistant'&&<><SChip l="Messages" v={s.details.total_messages??'—'} c="#00d4aa"/><SChip l="Aujourd'hui" v={s.details.today_messages??'—'} c="#00d4aa"/></>}
                    {s.service==='cv_generator'&&s.details.credits_used!==undefined&&<><SChip l="CVs générés" v={s.details.credits_used??0} c="#6366f1"/><SChip l="Crédits restants" v={s.details.balance??0} c="#6366f1"/></>}
                  </div>}
                </div>
              ))}
            </div>
          </div>}
        </div>}
      </div>
    </div>
  );
}
const SChip=({l,v,c})=>(
  <div className="rounded-xl p-2.5 text-center" style={{background:`${c}12`,border:`1px solid ${c}25`}}>
    <div className="font-black text-base" style={{color:c}}>{v}</div>
    <div className="text-xs mt-0.5 font-medium" style={{color:`${c}70`}}>{l}</div>
  </div>
);

// ─── Growth Panel ─────────────────────────────────────────────────────────────
function GrowthPanel({onClose}) {
  const [period,setPeriod]=useState('month'),[from,setFrom]=useState(''),[to,setTo]=useState('');
  const [data,setData]=useState(null),[loading,setLoading]=useState(false);
  const load=useCallback(async()=>{
    setLoading(true);
    try{let url=`${API_URL}/tracking/admin/growth?period=${period}`;
    if(period==='custom'&&from&&to) url+=`&from=${from}&to=${to}`;
    const r=await fetch(url,{headers:authH()});setData(await r.json());}catch(_){}
    setLoading(false);
  },[period,from,to]);
  useEffect(()=>{load();},[period]);
  const chartData=data?.growth||[],maxV=Math.max(...chartData.map(d=>d.new_users||0),1);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{background:'rgba(10,6,24,0.85)',backdropFilter:'blur(12px)'}}>
      <div className="absolute inset-0" onClick={onClose}/>
      <div className="um-fadeup relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-3xl overflow-hidden z-10"
        style={{background:'#1e1540',border:'1px solid rgba(16,185,129,0.25)',boxShadow:'0 24px 64px rgba(0,0,0,0.6)'}}>
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{borderBottom:'1px solid rgba(139,92,246,0.15)'}}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{background:'linear-gradient(135deg,#059669,#10b981)'}}><TrendingUp size={16} className="text-white"/></div>
            <div><div style={{color:C.text}} className="font-bold text-sm">Croissance utilisateurs</div><div style={{color:C.textSub}} className="text-xs">Inscrits par période</div></div>
          </div>
          <button onClick={onClose} className="um-btn w-8 h-8 rounded-xl flex items-center justify-center" style={{background:'rgba(255,255,255,0.06)',border:'1px solid rgba(139,92,246,0.2)'}}><X size={14} style={{color:C.textSub}}/></button>
        </div>
        <div className="flex gap-2 flex-wrap items-center px-5 py-3 flex-shrink-0" style={{borderBottom:'1px solid rgba(139,92,246,0.1)'}}>
          {[['week','7 jours'],['month','30 jours'],['year','1 an'],['custom','Personnalisé']].map(([v,l])=>(
            <Pip key={v} active={period===v} onClick={()=>setPeriod(v)} color="#10b981">{l}</Pip>
          ))}
          {period==='custom'&&<>
            <input type="date" value={from} onChange={e=>setFrom(e.target.value)} className="um-input px-3 py-1.5 rounded-xl text-xs"/>
            <input type="date" value={to} onChange={e=>setTo(e.target.value)} className="um-input px-3 py-1.5 rounded-xl text-xs"/>
            <button onClick={load} className="um-btn px-3 py-1.5 rounded-xl text-white text-xs font-bold" style={{background:'linear-gradient(135deg,#059669,#10b981)'}}>OK</button>
          </>}
        </div>
        <div className="overflow-y-auto um-scroll flex-1 p-5 space-y-4">
          {loading?<div className="flex justify-center py-12"><Spinner color="#10b981"/></div>:<>
            {data&&<div className="grid grid-cols-3 gap-3">
              {[{l:'Nouveaux',v:data.total_new,c:'#10b981'},{l:'Moy/jour',v:data.avg_per_day,c:'#8b5cf6'},{l:'Record',v:data.best_day?.count||0,c:'#f59e0b',sub:data.best_day?.date}].map((k,i)=>(
                <div key={i} className="rounded-2xl p-4 text-center" style={{background:`${k.c}12`,border:`1px solid ${k.c}25`}}>
                  <div className="text-2xl font-black" style={{color:k.c}}>{k.v}</div>
                  <div className="text-xs font-bold uppercase tracking-wider mt-1" style={{color:`${k.c}70`}}>{k.l}</div>
                  {k.sub&&<div className="text-xs mt-0.5" style={{color:`${k.c}50`}}>{k.sub}</div>}
                </div>
              ))}
            </div>}
            {chartData.length>0&&<div className="rounded-2xl p-4" style={{background:'rgba(16,185,129,0.06)',border:'1px solid rgba(16,185,129,0.15)'}}>
              <div className="text-xs font-bold uppercase tracking-widest mb-3" style={{color:C.textMuted}}>Évolution</div>
              <Sparkline data={chartData} color="#10b981" h={64}/>
            </div>}
            {chartData.length>0&&<div className="space-y-1">
              {[...chartData].reverse().filter(d=>d.new_users>0).map((d,i)=>(
                <div key={i} className="um-tr flex items-center justify-between px-3 py-2 rounded-xl">
                  <span className="text-xs um-mono" style={{color:C.textSub}}>{d.date}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-28"><MiniBar val={d.new_users} max={maxV} color="#10b981" h={3}/></div>
                    <span className="font-black text-xs um-mono w-5 text-right" style={{color:C.text}}>{d.new_users}</span>
                    <span className="text-xs um-mono w-20 text-right" style={{color:C.textMuted}}>↑ {d.cumulative}</span>
                  </div>
                </div>
              ))}
            </div>}
          </>}
        </div>
      </div>
    </div>
  );
}

// ─── User Drawer ──────────────────────────────────────────────────────────────
function UserDrawer({user,onClose,onToggle,onDelete,busy,onShowUsage}) {
  const [sessions,setSessions]=useState([]),[loadSess,setLoadSess]=useState(true);
  useEffect(()=>{
    const uid=user.$id||user.user_id;
    fetch(`${API_URL}/tracking/admin/user/${uid}/sessions`,{headers:authH()})
      .then(r=>r.json()).then(j=>{setSessions(j.sessions||[]);setLoadSess(false);}).catch(()=>setLoadSess(false));
  },[user]);
  const isAdmin=user.user_type==='admin',isActive=user.status==='active',sess=user._session;
  const accent=user._online?'#10b981':isAdmin?'#ef4444':'#8b5cf6';

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1" style={{background:'rgba(10,6,24,0.6)',backdropFilter:'blur(4px)'}} onClick={onClose}/>
      <div className="um-slidein w-full max-w-[460px] flex flex-col overflow-hidden"
        style={{background:'#150d2e',borderLeft:'1px solid rgba(139,92,246,0.2)',boxShadow:'-24px 0 80px rgba(0,0,0,0.7)'}}>

        {/* Barre couleur top */}
        <div style={{height:3,background:`linear-gradient(90deg,${accent},${accent}44,transparent)`}}/>

        {/* Header */}
        <div className="flex-shrink-0 px-6 py-5" style={{borderBottom:'1px solid rgba(139,92,246,0.12)'}}>
          <div className="flex items-start justify-between">
            <div className="flex gap-4">
              <div className="relative flex-shrink-0">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-black text-white"
                  style={{background:`linear-gradient(135deg,${accent},${accent}88)`,boxShadow:`0 8px 24px ${accent}35`}}>
                  {(user.name||user.email||'?')[0].toUpperCase()}
                </div>
                {user._online&&<div className="absolute -bottom-0.5 -right-0.5"><LiveDot size={10}/></div>}
              </div>
              <div>
                <div className="font-bold text-lg leading-tight" style={{color:C.text}}>{user.name||'—'}</div>
                <div className="text-sm mt-0.5 um-mono" style={{color:C.textSub}}>{user.email}</div>
                <div className="flex items-center gap-2 mt-2">
                  {user._online
                    ?<span className="flex items-center gap-1.5 text-xs font-bold text-emerald-400"><LiveDot size={6}/>En ligne</span>
                    :<span className="text-xs font-semibold" style={{color:C.textMuted}}>{isActive?'Hors ligne':'Désactivé'}</span>}
                  {isAdmin&&<span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{background:'rgba(239,68,68,0.15)',border:'1px solid rgba(239,68,68,0.3)',color:'#fca5a5'}}>Admin</span>}
                </div>
              </div>
            </div>
            <button onClick={onClose} className="um-btn w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{background:'rgba(255,255,255,0.06)',border:'1px solid rgba(139,92,246,0.2)'}}><X size={14} style={{color:C.textSub}}/></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto um-scroll">
          {/* Session active */}
          {user._online&&sess&&(
            <div className="m-4 rounded-2xl p-4" style={{background:'rgba(16,185,129,0.07)',border:'1px solid rgba(16,185,129,0.2)'}}>
              <div className="flex items-center gap-2 mb-3"><LiveDot size={6}/><span className="text-emerald-400 text-xs font-black uppercase tracking-widest">Session Active</span></div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                {[['📱 Plateforme',sess.platform||'—'],['📲 Appareil',sess.deviceModel||'—'],['⚡ Service',sess.service||'—'],['⏱ Durée',fmtDur(sess.durationSec)],['🌍 Localisation',[sess.city,sess.country].filter(Boolean).join(', ')||'—'],['🌐 IP',sess.ipAddress||'—']].map(([l,v],i)=>(
                  <div key={i}><div className="mb-0.5" style={{color:'rgba(52,211,153,0.5)'}}>{l}</div><div className="font-semibold capitalize" style={{color:'#6ee7b7'}}>{v}</div></div>
                ))}
              </div>
            </div>
          )}

          {/* Compte */}
          <div className="px-4 pb-3">
            <div className="text-xs font-black uppercase tracking-widest mb-2.5" style={{color:C.textMuted}}>Informations compte</div>
            <div className="rounded-2xl overflow-hidden" style={{border:'1px solid rgba(139,92,246,0.15)'}}>
              {[
                ['User ID',<span className="um-mono text-xs text-violet-400">{(user.$id||user.user_id||'—').slice(0,16)}…</span>],
                ['Type',isAdmin?'🛡 Admin':'👤 Client'],
                ['Statut',isActive?'✅ Activé':'🔴 Désactivé'],
                ['Inscrit le',fmt(user.created_at||user.$createdAt)],
                ['Dernière connexion',user.last_login?`${fmtAgo(user.last_login)} — ${fmtFull(user.last_login)}`:'—'],
                ['Pays',user.last_country||sess?.country||'—'],
                ['Ville',user.last_city||sess?.city||'—'],
              ].map(([l,v],i,arr)=>(
                <div key={i} className="flex items-center justify-between px-4 py-3 gap-3" style={{borderBottom:i<arr.length-1?'1px solid rgba(139,92,246,0.08)':'none',background:'rgba(255,255,255,0.02)'}}>
                  <span className="text-sm flex-shrink-0" style={{color:C.textSub}}>{l}</span>
                  <span className="text-sm font-semibold text-right" style={{color:C.text}}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Usage btn */}
          <div className="px-4 pb-3">
            <button onClick={()=>onShowUsage(user.$id||user.user_id,user.name||user.email)}
              className="um-btn w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-sm"
              style={{background:'rgba(139,92,246,0.12)',border:'1px solid rgba(139,92,246,0.25)',color:'#c4b5fd'}}>
              <BarChart2 size={15}/>Utilisation détaillée
            </button>
          </div>

          {/* Sessions */}
          <div className="px-4 pb-6">
            <div className="text-xs font-black uppercase tracking-widest mb-2.5" style={{color:C.textMuted}}>
              Historique sessions {sessions.length>0&&`(${sessions.length})`}
            </div>
            {loadSess?<div className="flex justify-center py-8"><Spinner/></div>
            :sessions.length===0?<div className="rounded-2xl py-10 text-center" style={{border:'1px solid rgba(139,92,246,0.1)'}}><Clock size={28} style={{color:C.textMuted}} className="mx-auto mb-2"/><p className="text-sm" style={{color:C.textMuted}}>Aucune session</p></div>
            :<div className="space-y-2">
              {sessions.map((s,i)=>{
                const active=s.is_active||s.isActive;
                return(
                  <div key={i} className="rounded-2xl p-3" style={{background:active?'rgba(16,185,129,0.06)':'rgba(255,255,255,0.03)',border:active?'1px solid rgba(16,185,129,0.2)':'1px solid rgba(139,92,246,0.1)'}}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">{active&&<LiveDot size={6}/>}<span className="text-xs font-semibold um-mono" style={{color:C.text}}>{fmtFull(s.startedAt)}</span></div>
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={active?{background:'rgba(16,185,129,0.15)',color:'#34d399'}:{background:'rgba(139,92,246,0.1)',color:C.textMuted}}>{active?'Active':fmtDur(s.duration)}</span>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      {[['OS',s.platform||'—'],['Service',s.service||'—'],['Pays',s.country||'—'],['Ville',s.city||'—']].map(([l,v],j)=>(
                        <div key={j}><div className="mb-0.5" style={{color:C.textMuted}}>{l}</div><div className="capitalize truncate" style={{color:C.textSub}}>{v}</div></div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>}
          </div>
        </div>

        {!isAdmin&&(
          <div className="flex-shrink-0 px-4 py-4 grid grid-cols-2 gap-2" style={{borderTop:'1px solid rgba(139,92,246,0.12)',background:'#150d2e'}}>
            <button onClick={()=>onToggle(user)} disabled={busy}
              className="um-btn flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-sm disabled:opacity-40"
              style={isActive?{background:'rgba(245,158,11,0.1)',border:'1px solid rgba(245,158,11,0.25)',color:'#fbbf24'}:{background:'rgba(16,185,129,0.1)',border:'1px solid rgba(16,185,129,0.25)',color:'#34d399'}}>
              {busy?<Spinner size={14} c={isActive?'#fbbf24':'#34d399'}/>:isActive?<Ban size={14}/>:<Power size={14}/>}
              {isActive?'Désactiver':'Réactiver'}
            </button>
            <button onClick={()=>onDelete(user)} disabled={busy}
              className="um-btn flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-sm disabled:opacity-40"
              style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.25)',color:'#f87171'}}>
              <Trash2 size={14}/>Supprimer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ════════════════════════════════════════════════════════════════════════════
export default function UserManagement() {
  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [busy,       setBusy]       = useState(false);
  const [showModal,  setShowModal]  = useState(false);
  const [search,     setSearch]     = useState('');
  const [ftType,     setFtType]     = useState('all');
  const [ftStatus,   setFtStatus]   = useState('all');
  const [ftLoc,      setFtLoc]      = useState('all');
  const [sortBy,     setSortBy]     = useState('usage');
  const [page,       setPage]       = useState(1);
  const [drawer,     setDrawer]     = useState(null);
  const [delUser,    setDelUser]    = useState(null);
  const [usageInfo,  setUsageInfo]  = useState(null);
  const [showGrowth, setShowGrowth] = useState(false);
  const [tick,       setTick]       = useState(new Date());
  const [onlineMap,  setOnlineMap]  = useState({});
  const [tStats,     setTStats]     = useState(null);
  const [usageCache, setUsageCache] = useState({});
  const [form, setForm] = useState({email:'',name:'',password:'',user_type:'client'});
  const timers = useRef({});

  const loadUsers=useCallback(async(silent=false)=>{
    if(!silent) setLoading(true);
    try{const r=await fetch(`${API_URL}/api/admin/users`,{headers:authH()});const d=await r.json();setUsers(d.users||[]);}
    catch{if(!silent) toast.error('Erreur chargement');}
    if(!silent) setLoading(false);
  },[]);

  const loadOnline=useCallback(async()=>{
    try{
      const [oR,sR]=await Promise.allSettled([
        fetch(`${API_URL}/tracking/admin/online`,{headers:authH()}).then(r=>r.json()),
        fetch(`${API_URL}/tracking/admin/stats`, {headers:authH()}).then(r=>r.json()),
      ]);
      if(oR.status==='fulfilled'&&oR.value?.online){const m={};oR.value.online.forEach(s=>{m[s.userId||s.user_id]=s;});setOnlineMap(m);}
      if(sR.status==='fulfilled'&&sR.value?.stats) setTStats(sR.value.stats);
    }catch(_){}
  },[]);

  const loadUsageAll=useCallback(async(list)=>{
    for(const u of list.slice(0,20)){
      const uid=u.$id||u.user_id;
      if(usageCache[uid]) continue;
      try{const r=await fetch(`${API_URL}/tracking/admin/user/${uid}/usage`,{headers:authH()});const j=await r.json();
      if(j.usage) setUsageCache(p=>({...p,[uid]:{totalTime:j.usage.total_time_sec||0,sessions:j.usage.session_count||0}}));}
      catch(_){}
      await new Promise(r=>setTimeout(r,80));
    }
  },[usageCache]);

  useEffect(()=>{
    loadUsers();loadOnline();
    timers.current.o=setInterval(loadOnline,10000);
    timers.current.u=setInterval(()=>loadUsers(true),30000);
    timers.current.t=setInterval(()=>setTick(new Date()),1000);
    return()=>Object.values(timers.current).forEach(clearInterval);
  },[]);
  useEffect(()=>{if(users.length>0) loadUsageAll(users);},[users]);

  const toggleStatus=async(user)=>{
    setBusy(true);const uid=user.$id||user.user_id,ns=user.status==='active'?'inactive':'active';
    try{const r=await fetch(`${API_URL}/api/admin/users/${uid}/status`,{method:'PATCH',headers:authH(),body:JSON.stringify({status:ns})});
    if(r.ok){setUsers(p=>p.map(u=>(u.$id||u.user_id)===uid?{...u,status:ns}:u));setDrawer(p=>p?{...p,status:ns}:null);toast.success(ns==='active'?'✅ Réactivé':'🚫 Désactivé');}else toast.error('Erreur');}catch{toast.error('Erreur réseau');}
    setBusy(false);
  };
  const doDelete=async()=>{
    if(!delUser)return;setBusy(true);const uid=delUser.$id||delUser.user_id;
    try{const r=await fetch(`${API_URL}/api/admin/users/${uid}`,{method:'DELETE',headers:authH()});
    if(r.ok){setUsers(p=>p.filter(u=>(u.$id||u.user_id)!==uid));setDrawer(null);setDelUser(null);toast.success('🗑 Supprimé');}else toast.error('Erreur');}catch{toast.error('Erreur réseau');}
    setBusy(false);
  };
  const doCreate=async(e)=>{
    e.preventDefault();toast.loading('Création...',{id:'c'});
    try{const r=await fetch(`${API_URL}/api/admin/users`,{method:'POST',headers:authH(),body:JSON.stringify(form)});
    if(r.ok){toast.success('✅ Créé !',{id:'c'});setShowModal(false);setForm({email:'',name:'',password:'',user_type:'client'});loadUsers();}
    else{const d=await r.json();toast.error(d.message||'Erreur',{id:'c'});}}catch{toast.error('Erreur réseau',{id:'c'});}
  };

  const enriched=users.map(u=>{const uid=u.$id||u.user_id,sess=onlineMap[uid],usage=usageCache[uid];return{...u,_online:!!sess,_session:sess||null,_totalTime:usage?.totalTime||0,_sessions:usage?.sessions||0};});
  const onlineCount=Object.keys(onlineMap).length;
  const today=new Date().toDateString();
  const countries=[...new Set(enriched.map(u=>u.last_country||u._session?.country).filter(Boolean))];

  const filtered=enriched
    .filter(u=>{
      const s=search.toLowerCase();
      return(!search||u.name?.toLowerCase().includes(s)||u.email?.toLowerCase().includes(s))
        &&(ftType==='all'||(ftType==='client'&&u.user_type!=='admin')||(ftType==='admin'&&u.user_type==='admin'))
        &&(ftStatus==='all'||(ftStatus==='online'&&u._online)||(ftStatus==='active'&&!u._online&&u.status==='active')||(ftStatus==='inactive'&&u.status==='inactive'))
        &&(ftLoc==='all'||u.last_country===ftLoc||u._session?.country===ftLoc);
    })
    .sort((a,b)=>{
      if(sortBy==='usage'){if(a._online!==b._online)return a._online?-1:1;if(b._totalTime!==a._totalTime)return b._totalTime-a._totalTime;return b._sessions-a._sessions;}
      if(sortBy==='sessions') return b._sessions-a._sessions;
      if(sortBy==='activity'){if(a._online!==b._online)return a._online?-1:1;return new Date(b.last_login||0)-new Date(a.last_login||0);}
      if(sortBy==='created') return new Date(b.created_at||0)-new Date(a.created_at||0);
      if(sortBy==='name')    return(a.name||'').localeCompare(b.name||'');
      return 0;
    });

  const totalPages=Math.ceil(filtered.length/PAGE_SIZE)||1;
  const paginated=filtered.slice((page-1)*PAGE_SIZE,page*PAGE_SIZE);
  const maxUsageTime=Math.max(...filtered.map(u=>u._totalTime),1);

  const exportCsv=()=>{
    const rows=[['Nom','Email','Type','Statut','En ligne','Pays','Ville','Inscrit','Temps','Sessions']];
    filtered.forEach(u=>rows.push([u.name||'',u.email||'',u.user_type||'',u.status||'',u._online?'OUI':'NON',u.last_country||'',u.last_city||'',fmt(u.created_at),fmtDur(u._totalTime),u._sessions]));
    const a=document.createElement('a');a.href=URL.createObjectURL(new Blob([rows.map(r=>r.map(c=>`"${c}"`).join(',')).join('\n')],{type:'text/csv'}));a.download=`users_${new Date().toISOString().slice(0,10)}.csv`;a.click();
  };

  const KPIS = [
    {label:'En ligne',   value:onlineCount,  icon:Wifi,        gradient:['#0d9488','#10b981'], live:true, sub:'temps réel'},
    {label:'Total',      value:users.length, icon:Users,       gradient:['#4f46e5','#6366f1']},
    {label:'Clients',    value:users.filter(u=>u.user_type!=='admin').length, icon:UserIcon, gradient:['#7c3aed','#a855f7']},
    {label:'Admins',     value:users.filter(u=>u.user_type==='admin').length, icon:Shield,   gradient:['#dc2626','#ef4444']},
    {label:'Activés',    value:users.filter(u=>u.status==='active').length,   icon:CheckCircle2, gradient:['#0284c7','#0ea5e9']},
    {label:"Aujourd'hui",value:users.filter(u=>u.created_at&&new Date(u.created_at).toDateString()===today).length, icon:TrendingUp, gradient:['#d97706','#f59e0b'], live:true},
  ];

  return (
    <>
      <style>{CSS}</style>
      <Toaster position="top-right" toastOptions={{style:{background:'#1e1540',color:C.text,border:'1px solid rgba(139,92,246,0.25)',boxShadow:'0 8px 32px rgba(0,0,0,0.5)'}}}/>

      <div className="um flex flex-col" style={{height:'100vh',background:C.bg,overflow:'hidden'}}>

        {/* ══ ZONE SUPÉRIEURE scrollable ══ */}
        <div className="flex-shrink-0 overflow-y-auto um-scroll">

          {/* Header */}
          <div className="px-6 pt-6 pb-0">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{background:'linear-gradient(135deg,#7c3aed,#ec4899)',boxShadow:'0 8px 24px rgba(124,58,237,0.4)'}}>
                    <Users size={22} className="text-white"/>
                  </div>
                  <div>
                    <h1 className="text-2xl font-black" style={{color:C.text}}>Gestion Utilisateurs</h1>
                    <p className="text-sm" style={{color:C.textSub}}>
                      <span className="inline-flex items-center gap-1.5">
                        <Activity size={11}/>
                        Suivi en temps réel · {filtered.length} résultat{filtered.length>1?'s':''}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={()=>setShowGrowth(true)} className="um-btn flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold"
                  style={{background:'rgba(16,185,129,0.12)',border:'1px solid rgba(16,185,129,0.25)',color:'#34d399'}}>
                  <TrendingUp size={14}/>Croissance
                </button>
                <button onClick={()=>{loadUsers();loadOnline();}} className="um-btn flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold"
                  style={{background:'rgba(255,255,255,0.06)',border:'1px solid rgba(139,92,246,0.2)',color:C.textSub}}>
                  <RefreshCw size={14} className={loading?'um-spin':''}/>Actualiser
                </button>
                <button onClick={exportCsv} className="um-btn flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold"
                  style={{background:'rgba(255,255,255,0.06)',border:'1px solid rgba(139,92,246,0.2)',color:C.textSub}}>
                  <Download size={14}/>CSV
                </button>
                <button onClick={()=>setShowModal(true)} className="um-btn flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white"
                  style={{background:'linear-gradient(135deg,#7c3aed,#ec4899)',boxShadow:'0 4px 16px rgba(124,58,237,0.4)'}}>
                  <Plus size={14}/>Nouveau
                </button>
              </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-6 gap-3 mb-4">
              {KPIS.map((k,i)=><KpiCard key={i} {...k} idx={i}/>)}
            </div>

            {/* Stats tracking */}
            {tStats&&(
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="rounded-2xl p-4" style={{background:C.card,border:`1px solid ${C.border}`}}>
                  <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{color:C.textMuted}}>Sessions aujourd'hui</div>
                  <div className="text-2xl font-black" style={{color:C.text}}>{tStats.sessionsTodaly??'—'}</div>
                  <div className="text-xs mt-0.5" style={{color:C.textSub}}>{tStats.uniqueToday??0} users uniques</div>
                </div>
                <div className="rounded-2xl p-4" style={{background:C.card,border:`1px solid ${C.border}`}}>
                  <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{color:C.textMuted}}>Durée moy.</div>
                  <div className="text-2xl font-black" style={{color:C.text}}>{fmtDur(tStats.avgDurationSec)}</div>
                  <div className="text-xs mt-0.5" style={{color:C.textSub}}>par session</div>
                </div>
                <div className="rounded-2xl p-4" style={{background:C.card,border:`1px solid ${C.border}`}}>
                  <div className="text-xs font-bold uppercase tracking-widest mb-2 flex items-center gap-1" style={{color:C.textMuted}}><Smartphone size={10}/>Plateformes</div>
                  {Object.entries(tStats.platforms||{}).slice(0,2).map(([p,c])=>(
                    <div key={p} className="mb-1.5">
                      <div className="flex justify-between text-xs mb-1"><span style={{color:C.textSub}} className="capitalize">{p||'?'}</span><span style={{color:C.text}} className="font-bold">{c}</span></div>
                      <MiniBar val={c} max={Math.max(...Object.values(tStats.platforms||{1:1}))} color="#8b5cf6" h={3}/>
                    </div>
                  ))}
                </div>
                <div className="rounded-2xl p-4" style={{background:C.card,border:`1px solid ${C.border}`}}>
                  <div className="text-xs font-bold uppercase tracking-widest mb-2 flex items-center gap-1" style={{color:C.textMuted}}><Globe size={10}/>Top pays</div>
                  {(tStats.topCountries||[]).slice(0,2).map((c,i)=>(
                    <div key={i} className="mb-1.5">
                      <div className="flex justify-between text-xs mb-1"><span style={{color:C.textSub}}>{c.country||'?'}</span><span style={{color:C.text}} className="font-bold">{c.count}</span></div>
                      <MiniBar val={c.count} max={(tStats.topCountries?.[0]?.count)||1} color="#10b981" h={3}/>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Online chips */}
            {onlineCount>0&&(
              <div className="rounded-2xl px-4 py-3 mb-4 flex flex-wrap gap-2 items-center"
                style={{background:'rgba(16,185,129,0.06)',border:'1px solid rgba(16,185,129,0.15)'}}>
                <div className="flex items-center gap-1.5 mr-1 flex-shrink-0">
                  <LiveDot size={6}/><span className="text-emerald-400 text-xs font-black uppercase tracking-widest">Actifs</span>
                </div>
                {Object.entries(onlineMap).map(([uid,sess])=>{
                  const u=users.find(x=>(x.$id||x.user_id)===uid);
                  const name=u?.name||u?.email?.split('@')[0]||uid.slice(0,8);
                  const eu=enriched.find(x=>(x.$id||x.user_id)===uid);
                  return(
                    <button key={uid} onClick={()=>eu&&setDrawer(eu)}
                      className="um-btn flex items-center gap-2 px-3 py-1.5 rounded-xl"
                      style={{background:'rgba(16,185,129,0.1)',border:'1px solid rgba(16,185,129,0.2)'}}>
                      <div className="w-5 h-5 rounded-lg flex items-center justify-center text-white text-xs font-black flex-shrink-0" style={{background:'linear-gradient(135deg,#059669,#10b981)'}}>{name[0].toUpperCase()}</div>
                      <div>
                        <div className="text-emerald-300 text-xs font-bold">{name}</div>
                        <div className="text-xs capitalize" style={{color:'rgba(52,211,153,0.5)'}}>{[sess.service,sess.platform,fmtDur(sess.durationSec)].filter(Boolean).join(' · ')}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Recherche */}
            <div className="relative mb-4">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{color:C.textMuted}}/>
              <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}}
                placeholder="Rechercher par nom, email…"
                className="um-input w-full pl-11 pr-10 py-3 rounded-2xl text-sm"/>
              {search&&<button onClick={()=>setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2"><X size={14} style={{color:C.textMuted}}/></button>}
            </div>

            {/* Filtres */}
            <div className="flex gap-2 flex-wrap items-center pb-4">
              <div className="flex gap-1.5">
                {[['all','Tous'],['client','Clients'],['admin','Admins']].map(([v,l])=>(
                  <Pip key={v} active={ftType===v} onClick={()=>{setFtType(v);setPage(1);}}>{l}</Pip>
                ))}
              </div>
              <div style={{width:1,height:20,background:'rgba(139,92,246,0.2)'}}/>
              <div className="flex gap-1.5">
                {[['all','Tous'],['online','🟢 En ligne'],['active','Actifs'],['inactive','Désactivés']].map(([v,l])=>(
                  <Pip key={v} active={ftStatus===v} onClick={()=>{setFtStatus(v);setPage(1);}} color="#a855f7">{l}</Pip>
                ))}
              </div>
              <div style={{width:1,height:20,background:'rgba(139,92,246,0.2)'}}/>
              {countries.length>0&&(
                <select value={ftLoc} onChange={e=>{setFtLoc(e.target.value);setPage(1);}}
                  className="um-pill px-3 py-1.5 rounded-xl text-xs font-semibold focus:outline-none cursor-pointer"
                  style={{background:'rgba(255,255,255,0.05)',border:'1px solid rgba(139,92,246,0.2)',color:C.textSub}}>
                  <option value="all">🌍 Tous pays</option>
                  {countries.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              )}
              <select value={sortBy} onChange={e=>{setSortBy(e.target.value);setPage(1);}}
                className="um-pill px-3 py-1.5 rounded-xl text-xs font-semibold focus:outline-none cursor-pointer"
                style={{background:'rgba(255,255,255,0.05)',border:'1px solid rgba(139,92,246,0.2)',color:C.textSub}}>
                <option value="usage">📊 Plus actifs</option>
                <option value="sessions">🔄 Nb sessions</option>
                <option value="activity">⏰ Activité</option>
                <option value="created">📅 Récents</option>
                <option value="name">🔤 Nom A→Z</option>
              </select>
              <span className="text-xs ml-auto um-mono" style={{color:C.textMuted}}>{fmtTime(tick)}</span>
            </div>
          </div>
        </div>

        {/* ══ TABLEAU — thead sticky dans son propre scroll ══ */}
        <div className="flex-1 min-h-0 mx-6 mb-4 flex flex-col">
          <div className="flex-1 min-h-0 rounded-2xl overflow-hidden flex flex-col"
            style={{background:C.surface,border:`1px solid ${C.border}`}}>

            {/* Scroll container — thead sticky DEDANS */}
            <div className="flex-1 overflow-y-auto um-scroll min-h-0">
              <table className="w-full" style={{borderCollapse:'separate',borderSpacing:0,tableLayout:'fixed'}}>
                <colgroup>
                  <col style={{width:'22%'}}/><col style={{width:'18%'}}/><col style={{width:'10%'}}/><col style={{width:'16%'}}/><col style={{width:'13%'}}/><col style={{width:'10%'}}/><col style={{width:'11%'}}/><col style={{width:'90px'}}/>
                </colgroup>
                <thead className="um-thead">
                  <tr>
                    {['Utilisateur','Email','Statut','Utilisation','Localisation','Service','Inscrit',''].map((h,i)=>(
                      <th key={i} className={`px-4 py-3 text-left ${[1].includes(i)?'hidden md:table-cell':[2,3,4,5].includes(i)?'hidden lg:table-cell':''}`}>
                        <span className="text-xs font-black uppercase tracking-widest" style={{color:C.textMuted}}>{h}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading&&users.length===0?(
                    <tr><td colSpan={8} className="py-20 text-center"><div className="flex flex-col items-center gap-3"><Spinner size={32}/><span className="text-sm" style={{color:C.textSub}}>Chargement…</span></div></td></tr>
                  ):paginated.length===0?(
                    <tr><td colSpan={8} className="py-20 text-center text-sm" style={{color:C.textMuted}}>Aucun résultat</td></tr>
                  ):paginated.map((u,idx)=>{
                    const isAdmin=u.user_type==='admin',isActive=u.status==='active';
                    const rank=(page-1)*PAGE_SIZE+idx+1;
                    const isTop3=sortBy==='usage'&&rank<=3&&u._totalTime>0;
                    const acc=u._online?'#10b981':isAdmin?'#ef4444':'#8b5cf6';
                    return(
                      <tr key={u.$id||u.user_id} onClick={()=>setDrawer(u)} className="um-tr um-row"
                        style={{animationDelay:`${idx*.025}s`,borderBottom:'1px solid rgba(139,92,246,0.07)',background:isTop3?'rgba(245,158,11,0.04)':undefined}}>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="relative flex-shrink-0">
                              {isTop3&&<div className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full flex items-center justify-center z-10 text-white text-[10px] font-black"
                                style={{background:'linear-gradient(135deg,#f59e0b,#ef4444)',boxShadow:'0 0 10px rgba(245,158,11,0.5)'}}>{rank}</div>}
                              <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-black text-sm"
                                style={{background:`linear-gradient(135deg,${acc},${acc}80)`,boxShadow:`0 4px 12px ${acc}30`}}>
                                {(u.name||u.email||'?')[0].toUpperCase()}
                              </div>
                              {u._online&&<div className="absolute -bottom-0.5 -right-0.5"><LiveDot size={8}/></div>}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold truncate" style={{color:C.text}}>{u.name||'—'}</div>
                              <div className="text-xs truncate md:hidden um-mono" style={{color:C.textSub}}>{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 hidden md:table-cell"><span className="text-xs um-mono text-violet-400 truncate block">{u.email}</span></td>
                        <td className="px-4 py-3.5 hidden lg:table-cell">
                          {u._online?<div className="flex items-center gap-1.5"><LiveDot size={6}/><span className="text-emerald-400 text-xs font-bold">En ligne</span></div>
                          :<div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full" style={{background:isActive?'rgba(139,92,246,0.5)':'rgba(239,68,68,0.5)'}}/><span className="text-xs font-medium" style={{color:isActive?C.textSub:'#f87171'}}>{isActive?'Hors ligne':'Désactivé'}</span></div>}
                        </td>
                        <td className="px-4 py-3.5 hidden lg:table-cell">
                          <div style={{width:140}}>
                            <div className="flex justify-between items-center mb-1.5">
                              <span className="text-xs font-bold" style={{color:'#a78bfa'}}>{fmtDur(u._totalTime)}</span>
                              <span className="text-xs" style={{color:C.textMuted}}>{u._sessions}×</span>
                            </div>
                            <MiniBar val={u._totalTime} max={maxUsageTime} color={isTop3?'#f59e0b':'#8b5cf6'} h={3}/>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 hidden xl:table-cell">
                          {(u.last_country||u._session?.country)?<div><div className="text-xs font-semibold" style={{color:C.text}}>{u.last_country||u._session?.country}</div><div className="text-xs" style={{color:C.textMuted}}>{u.last_city||u._session?.city||''}</div></div>:<span style={{color:C.textMuted}}>—</span>}
                        </td>
                        <td className="px-4 py-3.5 hidden lg:table-cell">
                          {u._online&&u._session?.service?<span className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold w-fit capitalize"
                            style={{background:'rgba(16,185,129,0.12)',border:'1px solid rgba(16,185,129,0.25)',color:'#34d399'}}>
                            <Zap size={9}/>{u._session.service}</span>:<span style={{color:C.textMuted}}>—</span>}
                        </td>
                        <td className="px-4 py-3.5"><span className="text-xs um-mono" style={{color:C.textMuted}}>{fmt(u.created_at||u.$createdAt)}</span></td>
                        <td className="px-4 py-3.5" onClick={e=>e.stopPropagation()}>
                          {!isAdmin&&(
                            <div className="um-act flex gap-1">
                              <button onClick={()=>setUsageInfo({uid:u.$id||u.user_id,name:u.name||u.email})} className="um-btn w-7 h-7 rounded-lg flex items-center justify-center" style={{background:'rgba(139,92,246,0.12)',border:'1px solid rgba(139,92,246,0.2)'}} title="Stats"><BarChart2 size={13} style={{color:'#c4b5fd'}}/></button>
                              <button onClick={()=>toggleStatus(u)} className="um-btn w-7 h-7 rounded-lg flex items-center justify-center" style={isActive?{background:'rgba(245,158,11,0.1)',border:'1px solid rgba(245,158,11,0.2)'}:{background:'rgba(16,185,129,0.1)',border:'1px solid rgba(16,185,129,0.2)'}} title={isActive?'Désactiver':'Réactiver'}>{isActive?<Ban size={13} style={{color:'#fbbf24'}}/>:<Power size={13} style={{color:'#34d399'}}/>}</button>
                              <button onClick={()=>setDelUser(u)} className="um-btn w-7 h-7 rounded-lg flex items-center justify-center" style={{background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.15)'}} title="Supprimer"><Trash2 size={13} style={{color:'#f87171'}}/></button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex-shrink-0 flex items-center justify-between px-5 py-3"
              style={{borderTop:`1px solid ${C.border}`,background:C.surface}}>
              <span className="text-xs um-mono" style={{color:C.textMuted}}>{filtered.length} résultats · p.{page}/{totalPages}</span>
              <div className="flex items-center gap-1.5">
                {loading&&<Spinner size={14}/>}
                <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} className="um-btn w-7 h-7 rounded-lg flex items-center justify-center disabled:opacity-30" style={{background:'rgba(255,255,255,0.05)',border:`1px solid ${C.border}`}}><ChevronLeft size={13} style={{color:C.textSub}}/></button>
                {Array.from({length:Math.min(totalPages,5)},(_,i)=>{
                  const p=totalPages<=5?i+1:page<=3?i+1:page>=totalPages-2?totalPages-4+i:page-2+i;
                  return <button key={p} onClick={()=>setPage(p)} className="um-btn w-7 h-7 rounded-lg text-xs font-bold"
                    style={p===page?{background:'linear-gradient(135deg,#7c3aed,#a855f7)',color:'#fff',boxShadow:'0 4px 12px rgba(124,58,237,0.4)'}:{background:'rgba(255,255,255,0.05)',border:`1px solid ${C.border}`,color:C.textMuted}}>{p}</button>;
                })}
                <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page>=totalPages} className="um-btn w-7 h-7 rounded-lg flex items-center justify-center disabled:opacity-30" style={{background:'rgba(255,255,255,0.05)',border:`1px solid ${C.border}`}}><ChevronRight size={13} style={{color:C.textSub}}/></button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══ PANELS ══ */}
      {drawer&&<UserDrawer user={drawer} onClose={()=>setDrawer(null)} onToggle={toggleStatus} onDelete={u=>{setDelUser(u);setDrawer(null);}} busy={busy} onShowUsage={(uid,name)=>setUsageInfo({uid,name})}/>}
      {usageInfo&&<UsagePanel uid={usageInfo.uid} name={usageInfo.name} onClose={()=>setUsageInfo(null)}/>}
      {showGrowth&&<GrowthPanel onClose={()=>setShowGrowth(false)}/>}

      {/* Delete */}
      {delUser&&(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(10,6,24,0.85)',backdropFilter:'blur(12px)'}}>
          <div className="um-fadeup rounded-3xl p-7 w-full max-w-sm" style={{background:'#1e1540',border:'1px solid rgba(239,68,68,0.25)',boxShadow:'0 24px 64px rgba(0,0,0,0.7)'}}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{background:'rgba(239,68,68,0.12)',border:'1px solid rgba(239,68,68,0.25)'}}><Trash2 size={24} style={{color:'#f87171'}}/></div>
            <h3 className="font-black text-xl text-center mb-2" style={{color:C.text}}>Supprimer ?</h3>
            <p className="text-center font-bold mb-1 text-red-400">{delUser.name||delUser.email}</p>
            <p className="text-center text-sm mb-7" style={{color:C.textMuted}}>Action permanente et irréversible.</p>
            <div className="flex gap-3">
              <button onClick={()=>setDelUser(null)} className="um-btn flex-1 py-3 rounded-2xl font-semibold text-sm" style={{background:'rgba(255,255,255,0.05)',border:`1px solid ${C.border}`,color:C.textSub}}>Annuler</button>
              <button onClick={doDelete} disabled={busy} className="um-btn flex-1 py-3 rounded-2xl font-bold text-white text-sm flex items-center justify-center gap-2 disabled:opacity-50" style={{background:'linear-gradient(135deg,#dc2626,#be185d)',boxShadow:'0 4px 16px rgba(220,38,38,0.35)'}}>
                {busy?<Spinner size={14} c="#fff"/>:<Trash2 size={14}/>}Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create */}
      {showModal&&(
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{background:'rgba(10,6,24,0.85)',backdropFilter:'blur(12px)'}}>
          <div className="um-fadeup rounded-3xl p-7 w-full max-w-md" style={{background:'#1e1540',border:`1px solid ${C.border}`,boxShadow:'0 24px 64px rgba(0,0,0,0.7)'}}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{background:'linear-gradient(135deg,#7c3aed,#ec4899)'}}><Plus size={18} className="text-white"/></div>
                <h3 className="font-black text-lg" style={{color:C.text}}>Nouvel utilisateur</h3>
              </div>
              <button onClick={()=>setShowModal(false)} className="um-btn w-8 h-8 rounded-xl flex items-center justify-center" style={{background:'rgba(255,255,255,0.06)',border:`1px solid ${C.border}`}}><X size={14} style={{color:C.textSub}}/></button>
            </div>
            <form onSubmit={doCreate} className="space-y-4">
              {[{k:'name',l:'Nom complet',I:UserIcon,t:'text',p:'Jean Dupont'},{k:'email',l:'Email',I:Mail,t:'email',p:'jean@exemple.com'},{k:'password',l:'Mot de passe',I:Lock,t:'password',p:'••••••••'}].map(f=>(
                <div key={f.k}>
                  <label className="flex items-center gap-1.5 text-xs font-bold mb-1.5 uppercase tracking-widest" style={{color:C.textMuted}}><f.I size={11}/>{f.l}</label>
                  <input type={f.t} value={form[f.k]} onChange={e=>setForm({...form,[f.k]:e.target.value})} className="um-input w-full px-4 py-3 rounded-xl text-sm" placeholder={f.p} required/>
                </div>
              ))}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-bold mb-1.5 uppercase tracking-widest" style={{color:C.textMuted}}><Shield size={11}/>Type</label>
                <select value={form.user_type} onChange={e=>setForm({...form,user_type:e.target.value})} className="um-input w-full px-4 py-3 rounded-xl text-sm">
                  <option value="client">Client</option><option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={()=>setShowModal(false)} className="um-btn flex-1 py-3 rounded-2xl font-semibold text-sm" style={{background:'rgba(255,255,255,0.05)',border:`1px solid ${C.border}`,color:C.textSub}}>Annuler</button>
                <button type="submit" className="um-btn flex-1 py-3 rounded-2xl font-bold text-white text-sm" style={{background:'linear-gradient(135deg,#7c3aed,#ec4899)',boxShadow:'0 4px 16px rgba(124,58,237,0.4)'}}>Créer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}